import { ClickAwayListener, Tooltip } from "@mui/material";
import { ElementType, ReactNode, useCallback, useState } from "react";

import useMediaQuery from "@/features/shared/hooks/use-media-query";

type LayerLegendTooltipProps = {
  title: ReactNode;
  ariaLabel: string;
  children: ReactNode;
  noWidth?: boolean;
  onTriggerClick?: (e: React.MouseEvent) => void;
  Trigger?: ElementType;
};

export default function LayerLegendTooltip({
  title,
  ariaLabel,
  children,
  noWidth = false,
  onTriggerClick,
  Trigger = "span",
}: LayerLegendTooltipProps) {
  const [open, setOpen] = useState(false);
  const isTouchPrimary = useMediaQuery("(pointer: coarse)");
  const placement = isTouchPrimary ? "top" : "bottom";

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen((previousOpen) => !previousOpen);
      onTriggerClick?.(e);
    },
    [onTriggerClick],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      handleClick(e as unknown as React.MouseEvent);
    },
    [handleClick],
  );

  // Frosted `.legend-tooltip` styling is intentional for all layer tooltips (text and legend images).
  const tooltipClass = noWidth
    ? "legend-tooltip legend-tooltip--no-width"
    : "legend-tooltip";

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <span style={{ display: "inline-flex" }}>
        <Tooltip
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          disableTouchListener
          placement={placement}
          classes={{ tooltip: tooltipClass }}
          title={title}
        >
          <Trigger
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
          >
            {children}
          </Trigger>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
}
